import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { getBlogCanonicalUrl } from '../utils/canonical';
import {
  Clock,
  Calendar,
  User,
  Tag,
  Twitter,
  Linkedin,
  Copy,
  CheckCircle
} from 'lucide-react';

import { formatDate, getCategoryColor } from '../utils/blog';
import { BlogCard } from '../components/blog/BlogCard';
import { BlogPost } from '../types/blog';
import toast from 'react-hot-toast';
import 'highlight.js/styles/atom-one-dark.css';

// Import all blog post JSON files synchronously for SSG
// This uses Vite's glob import feature
const blogPostModules = import.meta.glob('../../public/blog-data/*.json', { eager: true });

// Build a map of slug -> post data
const postsMap = new Map<string, BlogPost>();
const allPosts: BlogPost[] = [];

for (const path in blogPostModules) {
  const module = blogPostModules[path] as { default?: BlogPost } | BlogPost;
  const post = (module as any).default || module;

  // Skip index.json
  if (path.includes('index.json')) {
    continue;
  }

  if (post && post.slug) {
    const transformedPost: BlogPost = {
      ...post,
      readingTime: typeof post.readingTime === 'object'
        ? post.readingTime
        : { text: `${post.readingTime} min read`, minutes: post.readingTime as number, time: 0, words: 0 }
    };
    postsMap.set(post.slug, transformedPost);
    allPosts.push(transformedPost);
  }
}

// Helper to get related posts
function getRelatedPosts(currentPost: BlogPost, limit: number = 3): BlogPost[] {
  const otherPosts = allPosts.filter(post => post.slug !== currentPost.slug);

  const scoredPosts = otherPosts.map(post => {
    let score = 0;
    if (post.category === currentPost.category) score += 10;
    const sharedTags = post.tags.filter(tag => currentPost.tags.includes(tag));
    score += sharedTags.length * 5;
    return { post, score };
  });

  return scoredPosts
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.post);
}

export const BlogPostSSG: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [copied, setCopied] = useState(false);

  // Get post data synchronously from the pre-loaded map
  const post = slug ? postsMap.get(slug) : null;

  // Get related posts
  const relatedPosts = useMemo(() => {
    return post ? getRelatedPosts(post) : [];
  }, [post]);

  const shareOnTwitter = () => {
    if (post && typeof window !== 'undefined') {
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`;
      window.open(url, '_blank');
    }
  };

  const shareOnLinkedin = () => {
    if (post && typeof window !== 'undefined') {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
      window.open(url, '_blank');
    }
  };

  const copyLink = async () => {
    if (typeof window === 'undefined') return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Copy failed');
    }
  };

  // 404 fallback for SSG - show not found message
  if (!post) {
    return (
      <>
        <Head>
          <title>Post Not Found | Accessibility Checker Blog</title>
          <meta name="robots" content="noindex" />
        </Head>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
            <Link to="/blog" className="text-blue-600 hover:underline">
              Back to Blog
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Generate structured data - use canonical URL for SSR compatibility
  const canonicalUrl = getBlogCanonicalUrl(post.slug);
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    author: {
      '@type': 'Person',
      name: post.author
    },
    datePublished: post.date,
    image: post.image,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    }
  };

  return (
    <>
      <Head>
        <title>{post.title} | Accessibility Checker Blog</title>
        <meta name="description" content={post.description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="article:author" content={post.author} />
        <meta property="article:published_time" content={post.date} />
        {post.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Head>

      <article className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-gradient-to-b from-gray-50 to-white border-b">
          <div className="container mx-auto px-4 py-8">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <Link
                    to="/"
                    className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
                  >
                    Home
                  </Link>
                </li>
                <li className="text-gray-400">/</li>
                <li>
                  <Link
                    to="/blog"
                    className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
                  >
                    Blog
                  </Link>
                </li>
                <li className="text-gray-400">/</li>
                <li className="text-gray-700" aria-current="page">
                  {post.title}
                </li>
              </ol>
            </nav>

            {/* Post Header */}
            <div className="max-w-4xl mx-auto">
              {/* Category */}
              <div className="mb-4">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(post.category)}`}>
                  {post.category}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {post.title}
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" aria-hidden="true" />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" aria-hidden="true" />
                  <time dateTime={post.date}>{formatDate(post.date)}</time>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" aria-hidden="true" />
                  <span>{post.readingTime.text || `${post.readingTime.minutes} min read`}</span>
                </div>
              </div>

              {/* Tags */}
              {post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {post.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      <Tag className="w-3 h-3" aria-hidden="true" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Share Buttons */}
              <div className="flex items-center gap-2 pb-6 border-b">
                <span className="text-gray-600 mr-2">Share:</span>
                <button
                  onClick={shareOnTwitter}
                  className="p-2 text-gray-600 hover:text-blue-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  aria-label="Share on Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </button>
                <button
                  onClick={shareOnLinkedin}
                  className="p-2 text-gray-600 hover:text-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </button>
                <button
                  onClick={copyLink}
                  className="p-2 text-gray-600 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  aria-label="Copy link"
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Main Content - Render Markdown */}
            <div className="prose prose-lg max-w-none mb-12">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
              >
                {post.content}
              </ReactMarkdown>
            </div>

            {/* CTA Section */}
            <div className="bg-blue-50 rounded-lg p-8 mb-12">
              <h2 className="text-2xl font-bold mb-4">Test Your Website's Accessibility</h2>
              <p className="text-gray-700 mb-6">
                Use our free accessibility checker to identify and fix issues on your website.
              </p>
              <Link
                to="/"
                className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Start Free Scan
              </Link>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {relatedPosts.map(relatedPost => (
                    <BlogCard key={relatedPost.slug} post={relatedPost} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </article>
    </>
  );
};
